import { Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";
import { formatToTwoDecimal } from "../../../../../utils/numberFormatter";
import { formatPartyName } from "../../../../../utils/export/exportMeta";
import PdfExportHeader from "../../../../../components/Export/PdfExportHeader";

const styles = StyleSheet.create({
  page: {
    paddingHorizontal: 30,
    paddingTop: 30,
    paddingBottom: 60,
  },
  poBlock: {
    marginBottom: 12,
    padding: 10,
    borderWidth: 2,
    borderColor: "#403E92",
    borderStyle: "solid",
    borderRadius: 4,
  },
  infoRow: {
    flexDirection: "row",
    marginBottom: 3,
  },
  infoLabel: {
    fontFamily: "Helvetica-Bold",
    fontSize: 8,
    color: "#403E92",
    width: 100,
  },
  infoValue: {
    fontSize: 8,
    flexGrow: 1,
    flexBasis: 0,
    paddingRight: 16,
  },
  itemListLabel: {
    fontFamily: "Helvetica-Bold",
    fontSize: 9,
    color: "#403E92",
    marginTop: 6,
    marginBottom: 4,
  },
  table: {
    display: "table",
    width: "auto",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "transparent",
    borderRightWidth: 0,
    borderBottomWidth: 0,
  },
  tableRow: {
    flexDirection: "row",
    minHeight: 20,
  },
  itemColHeader: {
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#403E92",
    borderLeftWidth: 0,
    borderTopWidth: 0,
    padding: 4,
    textAlign: "center",
    backgroundColor: "#403E92",
    color: "white",
  },
  itemCol: {
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    padding: 4,
    textAlign: "center",
  },
  tableCell: {
    fontSize: 8,
  },
  noData: {
    fontSize: 12,
    textAlign: "center",
    paddingVertical: 12,
  },
  footer: {
    position: "absolute",
    bottom: -20,
    left: 0,
    right: 0,
    textAlign: "right",
    fontSize: 8,
  },
});

const ITEM_COLUMNS = [
  { key: "productCode", label: "Product Code", width: "12%" },
  { key: "productName", label: "Product Name", width: "22%" },
  { key: "unitPrice", label: "Unit Price", width: "14%" },
  { key: "Quantity", label: "Quantity", width: "13%" },
  { key: "moisture", label: "Moisture", width: "12%" },
  { key: "net_weight", label: "Net Weight", width: "13%" },
  { key: "totalPrice", label: "Subtotal", width: "14%" },
];

// Shared with payableExcel.js so both files show the same values
export function formatWithCurrency(value, currency_name) {
  if (value === undefined || value === null) return "";
  return (currency_name ? `${currency_name} ` : "") + formatToTwoDecimal(value);
}

export function formatDate(value) {
  return value ? new Date(value).toLocaleDateString() : "";
}

export function getSupplierName(vendor) {
  if (!vendor) return "";
  return formatPartyName(vendor.company_name, vendor.fname, vendor.lname);
}

export function getDestination(domestic_type) {
  if (domestic_type === "local") return "Local";
  if (domestic_type === "overseas") return "Overseas";
  return "";
}

// `records` are already-loaded payables (see payableExportConfig) and `meta`
// comes from buildExportMeta. This file must not fetch, since it is rendered
// inside payablePdf.worker.jsx.
const PayablePdf = ({ records = [], meta }) => (
  <Document title={meta.fileName}>
    <Page size="A4" style={styles.page} orientation="landscape">
        <PdfExportHeader meta={meta} />

        {records.length > 0 ? (
          records.map((po, idx) => (
            <View key={po.id || idx} style={styles.poBlock} wrap={false}>
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Transaction Number</Text>
                <Text style={styles.infoValue}>
                  {po.client_transaction_id || ""}
                </Text>
                <Text style={styles.infoLabel}>Purchase Date</Text>
                <Text style={styles.infoValue}>
                  {formatDate(po.purchaseDate)}
                </Text>
              </View>
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Supplier</Text>
                <Text style={styles.infoValue}>
                  {getSupplierName(po.vendor)}
                </Text>
                <Text style={styles.infoLabel}>Receiving Warehouse</Text>
                <Text style={styles.infoValue}>{po.warehouse?.name || ""}</Text>
              </View>
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Destination</Text>
                <Text style={styles.infoValue}>
                  {getDestination(po.domestic_type)}
                </Text>
                <Text style={styles.infoLabel}>Deadline</Text>
                <Text style={styles.infoValue}>{formatDate(po.dueDate)}</Text>
              </View>
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Total Amount</Text>
                <Text style={styles.infoValue}>
                  {po.totalPrice !== undefined
                    ? formatWithCurrency(po.totalPrice, po.currency_name)
                    : ""}
                </Text>
              </View>

              <Text style={styles.itemListLabel}>Item List</Text>
              <View style={styles.table}>
                <View style={styles.tableRow} fixed>
                  {ITEM_COLUMNS.map((col) => (
                    <View
                      key={col.key}
                      style={[styles.itemColHeader, { width: col.width }]}
                    >
                      <Text style={styles.tableCell}>{col.label}</Text>
                    </View>
                  ))}
                </View>
                {po.items && po.items.length > 0 ? (
                  po.items.map((item, i) => (
                    <View
                      key={item.productCode || i}
                      style={styles.tableRow}
                      wrap={false}
                    >
                      <View
                        style={[
                          styles.itemCol,
                          { width: ITEM_COLUMNS[0].width },
                        ]}
                      >
                        <Text style={styles.tableCell}>{item.productCode}</Text>
                      </View>
                      <View
                        style={[
                          styles.itemCol,
                          { width: ITEM_COLUMNS[1].width },
                        ]}
                      >
                        <Text style={styles.tableCell}>{item.productName}</Text>
                      </View>
                      <View
                        style={[
                          styles.itemCol,
                          { width: ITEM_COLUMNS[2].width },
                        ]}
                      >
                        <Text style={styles.tableCell}>
                          {item.unitPrice !== undefined
                            ? formatWithCurrency(
                                item.unitPrice,
                                po.currency_name,
                              )
                            : ""}
                        </Text>
                      </View>
                      <View
                        style={[
                          styles.itemCol,
                          { width: ITEM_COLUMNS[3].width },
                        ]}
                      >
                        <Text style={styles.tableCell}>
                          {item.weight ?? ""}
                        </Text>
                      </View>
                      <View
                        style={[
                          styles.itemCol,
                          { width: ITEM_COLUMNS[4].width },
                        ]}
                      >
                        <Text style={styles.tableCell}>
                          {item.moisture ?? ""}
                        </Text>
                      </View>
                      <View
                        style={[
                          styles.itemCol,
                          { width: ITEM_COLUMNS[5].width },
                        ]}
                      >
                        <Text style={styles.tableCell}>
                          {item.net_weight ?? ""}
                        </Text>
                      </View>
                      <View
                        style={[
                          styles.itemCol,
                          { width: ITEM_COLUMNS[6].width },
                        ]}
                      >
                        <Text style={styles.tableCell}>
                          {item.totalPrice !== undefined
                            ? formatWithCurrency(
                                item.totalPrice,
                                po.currency_name,
                              )
                            : ""}
                        </Text>
                      </View>
                    </View>
                  ))
                ) : (
                  <View style={styles.tableRow}>
                    <View style={[styles.itemCol, { width: "100%" }]}>
                      <Text style={styles.tableCell}>No items</Text>
                    </View>
                  </View>
                )}
              </View>
            </View>
          ))
        ) : (
          <Text style={styles.noData}>No data</Text>
        )}

        <View fixed>
          <Text
            style={styles.footer}
            render={({ pageNumber, totalPages }) =>
              `Page ${pageNumber} of ${totalPages}`
            }
          />
        </View>
    </Page>
  </Document>
);

export default PayablePdf;
